export interface ExportResponseHeaders {
  get(name: string): string | null;
}

const CONTENT_DISPOSITION_HEADER = 'Content-Disposition';
const MAX_EXPORT_FILE_NAME_LENGTH = 200;
const ENCODED_FILE_NAME_PATTERN = /(?:^|;)\s*filename\*\s*=\s*([^;]+)/i;
const PLAIN_FILE_NAME_PATTERN = /(?:^|;)\s*filename\s*=\s*("[^"]*"|[^;]*)/i;
const PATH_SEPARATOR_PATTERN = /[/\\]/;
const RELATIVE_PATH_SEGMENTS = ['.', '..'];

export function generateExportFileName(prefix: string, extension: string): string {
  const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
  return `${prefix}_${timestamp}.${extension}`;
}

export function resolveExportFileName(
  headers: ExportResponseHeaders,
  fallbackPrefix: string,
  fallbackExtension: string,
): string {
  return serverFileNameFrom(headers) ?? generateExportFileName(fallbackPrefix, fallbackExtension);
}

function serverFileNameFrom(headers: ExportResponseHeaders): string | null {
  const header = headers.get(CONTENT_DISPOSITION_HEADER)?.trim() ?? '';

  if (header.length === 0) {
    return null;
  }

  const encoded = ENCODED_FILE_NAME_PATTERN.exec(header);

  if (encoded !== null) {
    const extendedValue = unquoted(encoded[1].trim());
    const decoded = extendedValue === null ? null : percentDecoded(extendedValue);

    return decoded === null ? null : acceptedFileName(decoded);
  }

  const plain = PLAIN_FILE_NAME_PATTERN.exec(header);

  if (plain === null) {
    return null;
  }

  const plainValue = unquoted(plain[1].trim());

  return plainValue === null ? null : acceptedFileName(plainValue);
}

function percentDecoded(extendedValue: string): string | null {
  const charsetEnd = extendedValue.indexOf("'");
  const languageEnd = charsetEnd < 0 ? -1 : extendedValue.indexOf("'", charsetEnd + 1);
  const encoded = languageEnd < 0 ? extendedValue : extendedValue.slice(languageEnd + 1);

  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

function acceptedFileName(value: string): string | null {
  const name = withoutNonPrintableCharacters(lastPathSegment(value)).trim();

  if (name.length === 0 || name.length > MAX_EXPORT_FILE_NAME_LENGTH) {
    return null;
  }

  return RELATIVE_PATH_SEGMENTS.includes(name) ? null : name;
}

function lastPathSegment(value: string): string {
  return value.split(PATH_SEPARATOR_PATTERN).pop() ?? '';
}

function withoutNonPrintableCharacters(value: string): string {
  return Array.from(value)
    .filter((character) => !isNonPrintable(character.codePointAt(0) ?? 0))
    .join('');
}

function isNonPrintable(code: number): boolean {
  return (
    code <= 0x1f ||
    (code >= 0x7f && code <= 0x9f) ||
    (code >= 0x200e && code <= 0x200f) ||
    (code >= 0x2028 && code <= 0x202e) ||
    (code >= 0x2066 && code <= 0x2069) ||
    code === 0xfeff
  );
}

function unquoted(value: string): string | null {
  const opensQuoted = value.startsWith('"');
  const closesQuoted = value.length >= 2 && value.endsWith('"');

  if (opensQuoted !== closesQuoted) {
    return null;
  }

  return opensQuoted ? value.slice(1, -1) : value;
}
